// ─────────────────────────────────────────────────────
// TIER PLACEMENT QUIZ — 10 questions, strict scoring
//
// Scoring philosophy:
//   A = 0  (no knowledge / new)
//   B = 1  (awareness only)
//   C = 2  (hands-on at junior level)
//   D = 4  (hands-on at senior level)
//
// Max score: 40
// Tier 1 (Foundation): 0–9   — default for beginners
// Tier 2 (Entry):     10–22  — real helpdesk/junior experience
// Tier 3 (Mid):       23–33  — 3+ years hands-on
// Tier 4 (Senior):    34–40  — senior/lead level
//
// Someone who answers B/C on a few questions stays at Tier 1.
// Tier 2 requires consistent C answers = real job experience.
// ─────────────────────────────────────────────────────

export const TIER_QUESTIONS = [
  {
    id: 'tq1',
    text: 'How would you describe your current IT experience?',
    options: [
      { id: 'a', text: 'No professional IT experience — completely new to the field', score: 0 },
      { id: 'b', text: 'Some self-study or hobbyist tinkering, nothing professional', score: 1 },
      { id: 'c', text: '1–2 years in a paid IT role — helpdesk, junior support, apprenticeship', score: 2 },
      { id: 'd', text: '3+ years in IT with real technical responsibilities day-to-day', score: 4 },
    ],
  },
  {
    id: 'tq2',
    text: "A user calls: their PC shows a black screen on boot. What do you do first?",
    options: [
      { id: 'a', text: "I'd Google it and follow whatever comes up first", score: 0 },
      { id: 'b', text: 'Ask if anything changed recently, try turning it off and on', score: 1 },
      { id: 'c', text: 'Check POST behaviour, try safe mode, isolate hardware vs OS', score: 2 },
      { id: 'd', text: 'Full structured diagnostic — BIOS, boot order, memory test, event logs', score: 4 },
    ],
  },
  {
    id: 'tq3',
    text: 'What does a subnet mask of 255.255.255.0 tell you?',
    options: [
      { id: 'a', text: "I don't know what a subnet mask is", score: 0 },
      { id: 'b', text: "I've heard of it but I couldn't explain it", score: 1 },
      { id: 'c', text: 'The network has 254 usable host addresses (/24)', score: 2 },
      { id: 'd', text: 'All of the above and I work with subnetting regularly at work', score: 4 },
    ],
  },
  {
    id: 'tq4',
    text: 'Have you administered Active Directory or a similar directory service in a real job?',
    options: [
      { id: 'a', text: "No — I've never touched it, or only know the name", score: 0 },
      { id: 'b', text: "I've read about it but only used it as an end user", score: 1 },
      { id: 'c', text: 'Yes — I create users, manage groups, apply GPOs at work', score: 2 },
      { id: 'd', text: 'Yes — including OUs, delegation, trusts, or hybrid Azure AD', score: 4 },
    ],
  },
  {
    id: 'tq5',
    text: 'Have you ever written a script to automate an IT task at work?',
    options: [
      { id: 'a', text: "No — I haven't done any scripting", score: 0 },
      { id: 'b', text: "I've copied and modified scripts but not written one myself", score: 1 },
      { id: 'c', text: 'Yes — I write PowerShell or Bash for real tasks in my job', score: 2 },
      { id: 'd', text: 'Yes — I automate deployments, provisioning, or complex workflows', score: 4 },
    ],
  },
  {
    id: 'tq6',
    text: 'Someone reports their WiFi drops every 20 minutes but wired works fine. Where do you start?',
    options: [
      { id: 'a', text: "I'm not sure — I'd ask someone or search online", score: 0 },
      { id: 'b', text: 'Restart the router and see if it helps', score: 1 },
      { id: 'c', text: 'Check driver, channel congestion, signal strength, DHCP lease time', score: 2 },
      { id: 'd', text: 'All of the above plus AP logs, roaming settings, packet capture if needed', score: 4 },
    ],
  },
  {
    id: 'tq7',
    text: 'Have you managed virtual machines in a professional environment?',
    options: [
      { id: 'a', text: "No — I've never worked with VMs at all", score: 0 },
      { id: 'b', text: "I've run VMs on my own laptop (VirtualBox, Parallels, etc.)", score: 1 },
      { id: 'c', text: 'Yes — I manage VMs on Hyper-V, VMware, or similar at work', score: 2 },
      { id: 'd', text: 'Yes — including clusters, HA, storage configuration, live migration', score: 4 },
    ],
  },
  {
    id: 'tq8',
    text: 'What is the purpose of DNS?',
    options: [
      { id: 'a', text: "I don't know", score: 0 },
      { id: 'b', text: 'It has something to do with websites and internet addresses', score: 1 },
      { id: 'c', text: 'It resolves hostnames to IP addresses — I configure it at work', score: 2 },
      { id: 'd', text: "All of the above and I manage DNS zones, records, and TTLs regularly", score: 4 },
    ],
  },
  {
    id: 'tq9',
    text: 'Which of these have you used professionally — not just read about?',
    options: [
      { id: 'a', text: 'None of these', score: 0 },
      { id: 'b', text: 'A ticketing system as an end user only (submitting tickets)', score: 1 },
      { id: 'c', text: 'A ticketing system as an agent (Jira, ServiceNow, Zendesk, etc.)', score: 2 },
      { id: 'd', text: 'MDM (Intune, Jamf), cloud admin (Azure, AWS), or multiple platforms', score: 4 },
    ],
  },
  {
    id: 'tq10',
    text: 'A user says their Outlook is slow. You remoted in and everything looks fine. What next?',
    options: [
      { id: 'a', text: "I'd tell them to restart and see if it helps", score: 0 },
      { id: 'b', text: "I'd check for updates and antivirus scans", score: 1 },
      { id: 'c', text: 'Check mailbox size, OST file, add-ins, connectivity to Exchange/M365', score: 2 },
      { id: 'd', text: 'All of the above plus profile rebuild, message store repair, network trace', score: 4 },
    ],
  },
]

// Strict thresholds — Tier 2 requires real consistent job experience
export function scoreToTier(score) {
  if (score <= 9)  return 1  // Foundation — default for beginners
  if (score <= 22) return 2  // Entry Level — consistent junior experience
  if (score <= 33) return 3  // Mid-Level — strong hands-on background
  return 4                   // Senior — lead/architect level
}

export function tierLabel(tier) {
  return ['', 'Foundation', 'Entry Level', 'Mid-Level', 'Senior'][tier]
}

// ─────────────────────────────────────────────────────
// TREE RECOMMENDATION QUIZ — 8 questions, post-capstone
// ─────────────────────────────────────────────────────

export const TREE_QUESTIONS = [
  {
    id: 'trq1',
    text: 'What kind of work do you find most satisfying?',
    options: [
      { id: 'a', text: 'Building and maintaining the systems that everything else runs on', trees: ['infrastructure'] },
      { id: 'b', text: 'Making sure IT services run smoothly and teams work efficiently', trees: ['itsm'] },
      { id: 'c', text: 'Working with cloud platforms and modern deployment pipelines', trees: ['cloud'] },
      { id: 'd', text: 'Finding threats, protecting systems, and managing risk', trees: ['security'] },
    ],
  },
  {
    id: 'trq2',
    text: 'Which day-to-day environment appeals to you most?',
    options: [
      { id: 'a', text: 'On-prem datacentres, racks, servers, cables, virtualisation', trees: ['infrastructure'] },
      { id: 'b', text: 'Process design, ticketing workflows, stakeholder management', trees: ['itsm'] },
      { id: 'c', text: 'Cloud consoles, infrastructure-as-code, containers, pipelines', trees: ['cloud'] },
      { id: 'd', text: 'Security dashboards, logs, alerts, incident response', trees: ['security'] },
    ],
  },
  {
    id: 'trq3',
    text: 'Which tools or concepts are you most drawn to learning next?',
    options: [
      { id: 'a', text: 'Active Directory deep dive, Hyper-V, VMware, enterprise networking', trees: ['infrastructure'] },
      { id: 'b', text: 'ITIL 4, ServiceNow, Jira Service Management, SLA management', trees: ['itsm'] },
      { id: 'c', text: 'Azure or AWS architecture, Terraform, Kubernetes, CI/CD pipelines', trees: ['cloud'] },
      { id: 'd', text: 'SIEM tools, firewalls, penetration testing, compliance frameworks', trees: ['security'] },
    ],
  },
  {
    id: 'trq4',
    text: 'When something breaks in production, what role do you want to play?',
    options: [
      { id: 'a', text: 'The person who diagnoses and fixes the underlying infrastructure', trees: ['infrastructure'] },
      { id: 'b', text: 'The person coordinating response and communicating to stakeholders', trees: ['itsm'] },
      { id: 'c', text: 'The person rebuilding or scaling the affected cloud service fast', trees: ['cloud'] },
      { id: 'd', text: "The person investigating whether it was an attack or breach", trees: ['security'] },
    ],
  },
  {
    id: 'trq5',
    text: 'Which certification path sounds most interesting to you?',
    options: [
      { id: 'a', text: 'Microsoft Certified: Azure Administrator or VMware VCP', trees: ['infrastructure', 'cloud'] },
      { id: 'b', text: 'ITIL 4 Foundation, ServiceNow Certified, or ITIL Practitioner', trees: ['itsm'] },
      { id: 'c', text: 'AWS Solutions Architect, Azure Architect Expert, GCP Professional', trees: ['cloud'] },
      { id: 'd', text: 'CompTIA Security+, CEH, CISSP, or SC-200 Security Operations', trees: ['security'] },
    ],
  },
  {
    id: 'trq6',
    text: 'How do you feel about data, reporting, and analysis?',
    options: [
      { id: 'a', text: "Not really — I prefer hands-on technical work over dashboards", trees: ['infrastructure'] },
      { id: 'b', text: "I like metrics when they help improve service quality", trees: ['itsm'] },
      { id: 'c', text: "I enjoy data pipelines and automation as part of infrastructure", trees: ['cloud'] },
      { id: 'd', text: "I like analysing logs and events to find patterns and threats", trees: ['security'] },
    ],
  },
  {
    id: 'trq7',
    text: "Where do you picture yourself in 3 years?",
    options: [
      { id: 'a', text: 'Infrastructure engineer or senior sysadmin running enterprise environments', trees: ['infrastructure'] },
      { id: 'b', text: 'IT service manager, ITSM consultant, or operations lead', trees: ['itsm'] },
      { id: 'c', text: 'Cloud engineer, DevOps engineer, or solutions architect', trees: ['cloud'] },
      { id: 'd', text: 'Security analyst, SOC engineer, or penetration tester', trees: ['security'] },
    ],
  },
  {
    id: 'trq8',
    text: "Which statement feels most true right now?",
    options: [
      { id: 'a', text: "I want to own and understand the full infrastructure stack", trees: ['infrastructure'] },
      { id: 'b', text: "I want to make IT services more reliable and better managed", trees: ['itsm'] },
      { id: 'c', text: "I want to build and operate scalable systems in the cloud", trees: ['cloud'] },
      { id: 'd', text: "I want to protect organisations from threats and breaches", trees: ['security'] },
    ],
  },
]

export function recommendTree(answers) {
  const scores = { infrastructure: 0, itsm: 0, cloud: 0, security: 0, 'data-analytics': 0 }
  TREE_QUESTIONS.forEach(q => {
    const opt = q.options.find(o => o.id === answers[q.id])
    if (opt) opt.trees.forEach(t => { scores[t] = (scores[t] || 0) + 1 })
  })
  return Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0]
}

export const CAREER_TREES = [
  { id: 'infrastructure', label: 'Infrastructure', icon: '🖥️',
    description: 'Servers, networking, Active Directory, virtualisation, on-prem and hybrid environments.',
    suits: 'Good if you want to work hands-on with physical and virtual infrastructure.' },
  { id: 'itsm', label: 'ITSM', icon: '🎫',
    description: 'IT service management — ServiceNow, Jira, ITIL, incident and change management.',
    suits: 'Good if you want to work in service delivery, operations, or IT process roles.' },
  { id: 'cloud', label: 'Cloud', icon: '☁️',
    description: 'Azure, AWS, GCP — cloud architecture, services, migrations, and cloud-native operations.',
    suits: 'Good if you want to specialise in cloud platforms and infrastructure-as-code.' },
  { id: 'security', label: 'Security', icon: '🔒',
    description: 'Cybersecurity fundamentals, threat detection, incident response, compliance, and SOC work.',
    suits: 'Good if you want to work in defensive security, risk, or compliance roles.' },
  { id: 'data-analytics', label: 'Data & Analytics', icon: '📊',
    description: 'Data infrastructure, SQL, BI tools, pipelines, and analytics engineering.',
    suits: 'Good if you want to work with data platforms, reporting, and analytics.' },
]
